import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  Users, GraduationCap, Wallet, TrendingUp, UserCheck, UserX,
  BookOpen, Layers3, PieChart as PieIcon, IndianRupee,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

const COLORS = ["#0A66C2", "#5B8DBE", "#8CB4D8", "#B9D4E8", "#DCE9F3"];

function KpiCard({ icon: Icon, label, value, sub, testid, accent = "#0A66C2" }) {
  return (
    <div className="linkedin-card p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="kpi-label">{label}</div>
          <div className="text-2xl font-semibold mt-1 text-[#000000E6]">{value}</div>
          {sub && <div className="text-xs text-[#00000099] mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{background: `${accent}15`, color: accent}}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-[#00000099]">Loading dashboard…</div>;
  const k = data.kpis;
  const funnel = Object.entries(data.lead_funnel).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Real-time overview of your coaching institute" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={GraduationCap} label="Total Students" value={k.total_students} sub={`+${k.new_admissions} this month`} testid="kpi-students" />
        <KpiCard icon={Users} label="Faculty" value={k.total_faculty} sub="Active teachers" accent="#0F5132" testid="kpi-faculty" />
        <KpiCard icon={PieIcon} label="Leads" value={k.total_leads} sub={`${k.conversion_rate}% conversion`} accent="#915907" testid="kpi-leads" />
        <KpiCard icon={Layers3} label="Batches" value={k.total_batches} sub={`${k.total_courses} courses`} accent="#5A2A82" testid="kpi-batches" />

        <KpiCard icon={IndianRupee} label="Total Fees" value={`₹${(k.total_fees/1000).toFixed(1)}K`} sub="Billed" testid="kpi-fees-total" />
        <KpiCard icon={Wallet} label="Collected" value={`₹${(k.collected/1000).toFixed(1)}K`} sub="This period" accent="#057642" testid="kpi-collected" />
        <KpiCard icon={TrendingUp} label="Due" value={`₹${(k.due/1000).toFixed(1)}K`} sub="Outstanding" accent="#CC1016" testid="kpi-due" />
        <KpiCard icon={UserCheck} label="Present Today" value={k.present_today} sub={`${k.absent_today} absent`} accent="#0F5132" testid="kpi-present" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="linkedin-card p-5 lg:col-span-2" data-testid="revenue-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#000000E6]">Revenue (last 6 months)</h3>
            <span className="text-xs text-[#00000099]">Payments received</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC" />
              <XAxis dataKey="month" stroke="#00000099" tick={{fontSize: 12}} />
              <YAxis stroke="#00000099" tick={{fontSize: 12}} />
              <Tooltip contentStyle={{background: "#fff", border: "1px solid #E0DFDC", borderRadius: 8}} />
              <Line type="monotone" dataKey="revenue" stroke="#0A66C2" strokeWidth={2.5} dot={{r:4, fill:"#0A66C2"}} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="linkedin-card p-5" data-testid="lead-funnel-chart">
          <h3 className="text-lg font-semibold text-[#000000E6] mb-4">Lead funnel</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={funnel} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {funnel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{fontSize: 12}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="linkedin-card p-5" data-testid="batch-occupancy-chart">
        <h3 className="text-lg font-semibold text-[#000000E6] mb-4">Weekly test performance (sample)</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={[
            {name:"Mon", avg:72}, {name:"Tue", avg:78}, {name:"Wed", avg:65},
            {name:"Thu", avg:81}, {name:"Fri", avg:74}, {name:"Sat", avg:88},
          ]}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC"/>
            <XAxis dataKey="name" stroke="#00000099" tick={{fontSize: 12}}/>
            <YAxis stroke="#00000099" tick={{fontSize: 12}}/>
            <Tooltip contentStyle={{background:"#fff", border:"1px solid #E0DFDC", borderRadius:8}}/>
            <Bar dataKey="avg" fill="#0A66C2" radius={[6,6,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
